import React from "react";
import { createPortal } from "react-dom";
import { Modal } from "@shared/controls/Modal";

export interface Guest {
  adults: number;
  children: number;
  infants: number;
  pets: number;
}

interface GuestsModalProps {
  show: boolean;
  onClose: () => void;
  guests: Guest;
  onGuestsChange: (guests: Guest) => void;
  maxGuests?: number;
  maxPets?: number;
  title?: string;
  subtitle?: string;
}

export const GuestsModal: React.FC<GuestsModalProps> = ({
  show,
  onClose,
  guests,
  onGuestsChange,
  maxGuests = 5,
  maxPets = 0,
  title = "Guests",
  subtitle,
}) => {
  const handleAdultChange = (delta: number) => {
    const newAdults = Math.max(1, guests.adults + delta);
    const totalGuests = newAdults + guests.children;

    if (totalGuests <= maxGuests) {
      onGuestsChange({ ...guests, adults: newAdults });
    }
  };

  const handleChildrenChange = (delta: number) => {
    const newChildren = Math.max(0, guests.children + delta);
    const totalGuests = newChildren + guests.adults;

    if (totalGuests <= maxGuests) {
      onGuestsChange({ ...guests, children: newChildren });
    }
  };

  const handleInfantsChange = (delta: number) => {
    const newInfants = Math.max(0, Math.min(5, guests.infants + delta));
    onGuestsChange({ ...guests, infants: newInfants });
  };

  const handlePetsChange = (delta: number) => {
    const newPets = Math.max(0, Math.min(maxPets, guests.pets + delta));
    onGuestsChange({ ...guests, pets: newPets });
  };

  const totalGuests = guests.adults + guests.children;

  const modalContent = (
    <Modal
      show={show}
      onClose={onClose}
      size={500}
      className="modal-high-zindex"
      backdrop={true}
    >
      <div className="p-4">
        <h5 className="fw-bold font-onest mb-4">{title}</h5>

        {subtitle && (
          <p className="text-muted small font-onest mb-4">{subtitle}</p>
        )}

        {/* Adults */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <div className="fw-semibold font-onest">Adults</div>
            <div className="text-muted small font-onest">Age 18+</div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-secondary rounded-circle"
              style={{ width: "36px", height: "36px" }}
              onClick={() => handleAdultChange(-1)}
              disabled={guests.adults <= 1}
            >
              <i className="fas fa-minus"></i>
            </button>
            <span
              className="fw-semibold"
              style={{ minWidth: "30px", textAlign: "center" }}
            >
              {guests.adults}
            </span>
            <button
              className="btn btn-outline-secondary rounded-circle"
              style={{ width: "36px", height: "36px" }}
              onClick={() => handleAdultChange(1)}
              disabled={totalGuests >= maxGuests}
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>

        {/* Children */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <div className="fw-semibold font-onest">Children</div>
            <div className="text-muted small font-onest">Ages 2-17</div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-secondary rounded-circle"
              style={{ width: "36px", height: "36px" }}
              onClick={() => handleChildrenChange(-1)}
              disabled={guests.children <= 0}
            >
              <i className="fas fa-minus"></i>
            </button>
            <span
              className="fw-semibold"
              style={{ minWidth: "30px", textAlign: "center" }}
            >
              {guests.children}
            </span>
            <button
              className="btn btn-outline-secondary rounded-circle"
              style={{ width: "36px", height: "36px" }}
              onClick={() => handleChildrenChange(1)}
              disabled={totalGuests >= maxGuests}
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>

        {/* Infants */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <div className="fw-semibold font-onest">Infants</div>
            <div className="text-muted small font-onest">Under 2</div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-secondary rounded-circle"
              style={{ width: "36px", height: "36px" }}
              onClick={() => handleInfantsChange(-1)}
              disabled={guests.infants <= 0}
            >
              <i className="fas fa-minus"></i>
            </button>
            <span
              className="fw-semibold"
              style={{ minWidth: "30px", textAlign: "center" }}
            >
              {guests.infants}
            </span>
            <button
              className="btn btn-outline-secondary rounded-circle"
              style={{ width: "36px", height: "36px" }}
              onClick={() => handleInfantsChange(1)}
              disabled={guests.infants >= 5}
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>

        {/* Pets */}
        {maxPets > 0 && (
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-semibold font-onest">Pets</div>
              <div className="text-muted small font-onest">Max {maxPets}</div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-outline-secondary rounded-circle"
                style={{ width: "36px", height: "36px" }}
                onClick={() => handlePetsChange(-1)}
                disabled={guests.pets <= 0}
              >
                <i className="fas fa-minus"></i>
              </button>
              <span
                className="fw-semibold font-onest"
                style={{ minWidth: "30px", textAlign: "center" }}
              >
                {guests.pets}
              </span>
              <button
                className="btn btn-outline-secondary rounded-circle"
                style={{ width: "36px", height: "36px" }}
                onClick={() => handlePetsChange(1)}
                disabled={guests.pets >= maxPets}
              >
                <i className="fas fa-plus"></i>
              </button>
            </div>
          </div>
        )}

        <div className="mt-4 pt-3 border-top">
          <button
            className="btn btn-primary w-100 rounded-3 py-2"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );

  return show ? createPortal(modalContent, document.body) : null;
};
